import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthContext } from '@/context/AuthContext';
import AiChatDrawer from '@/storybook/components/AiChatDrawer/AiChatDrawer';
import ChatLayout from '@/storybook/components/AiChatDrawer/components/ChatLayout/ChatLayout';
import ChatFilterHeader from '@/storybook/components/AiChatDrawer/components/ChatFilterHeader/ChatFilterHeader';
import { FIRST_TIME_DISCLAIMER_SESSION_STORAGE_KEY } from '@/storybook/components/AiChatDrawer/components/ChatLayout/ChatLayout.constants';
import useChatbotStore from '../store/useChatbotStore';
import useChatbot from '../hooks/useChatbot';
import useChatbotWelcome from '../hooks/useChatbotWelcome';
import useChatbotFilterHeader from '../hooks/useChatbotFilterHeader';
import ChatHistoryPanel from '../ChatHistory/ChatHistoryPanel';
import ChatConversationContent from './ChatConversationContent';
import RateLimitStatus from './RateLimitStatus/RateLimitStatus';
import { normalizeRateLimit } from './RateLimitStatus/rateLimit.utils';
import styles from './ChatDrawer.module.css';
import type { ChatbotActiveFilters, ChatbotMessage } from '@/models/chatbot';

const formatResetTime = (resetEpoch: number): string => {
  const resetDate = new Date(resetEpoch * 1000);

  return resetDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

const joinMessages = (messages: ReadonlyArray<string | null>): string | undefined => {
  const visibleMessages = messages.filter(
    (message): message is string => typeof message === 'string' && message.length > 0,
  );

  return visibleMessages.length > 0 ? visibleMessages.join(' ') : undefined;
};

const ChatDrawer: FC = () => {
  const { t } = useTranslation();
  const { userInfo } = useAuthContext();

  const messages = useChatbotStore(s => s.messages);
  const streamingContent = useChatbotStore(s => s.streamingContent);
  const streamingFiles = useChatbotStore(s => s.streamingFiles);
  const streamingReasoningSteps = useChatbotStore(s => s.streamingReasoningSteps);
  const streamingPromptFilters = useChatbotStore(s => s.streamingPromptFilters);
  const isStreaming = useChatbotStore(s => s.isStreaming);
  const isStopping = useChatbotStore(s => s.isStopping);
  const isDrawerOpen = useChatbotStore(s => s.isDrawerOpen);
  const error = useChatbotStore(s => s.error);
  const activeConversationId = useChatbotStore(s => s.activeConversationId);
  const rateLimit = useChatbotStore(s => normalizeRateLimit(s.rateLimit));

  const { isConversationBusy, sendMessage, retryLastMessage, stopMessage, handleNewChat, handleCloseDrawer } =
    useChatbot();
  const [injectedValue, setInjectedValue] = useState<{ value: string; key: number } | undefined>(undefined);
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const historyButtonRef = useRef<HTMLButtonElement>(null);
  const wasHistoryOpenRef = useRef(false);

  useEffect(() => {
    if (wasHistoryOpenRef.current && !isHistoryOpen) {
      historyButtonRef.current?.focus();
    }

    wasHistoryOpenRef.current = isHistoryOpen;
  }, [isHistoryOpen]);

  const handleQuestionClick = useCallback((question: string) => {
    setInjectedValue({ value: question, key: Date.now() });
  }, []);

  const handleSendMessage = useCallback(
    async (message: string) => {
      setInjectedValue(undefined);
      await sendMessage(message);
    },
    [sendMessage],
  );

  const handleStartNewChat = useCallback(() => {
    setInjectedValue(undefined);
    handleNewChat();
  }, [handleNewChat]);

  const handleDrawerClose = useCallback(() => {
    setInjectedValue(undefined);
    handleCloseDrawer();
  }, [handleCloseDrawer]);

  const handleOpenHistory = useCallback(() => setIsHistoryOpen(true), []);

  const handleCloseHistory = useCallback(() => setIsHistoryOpen(false), []);

  const handleHistoryNewChat = useCallback(() => {
    setIsHistoryOpen(false);
    setInjectedValue(undefined);
    handleNewChat();
  }, [handleNewChat]);

  const handleSelectConversation = useCallback(
    (
      conversationId: string,
      conversationMessages: ChatbotMessage[],
      conversationFilters: ChatbotActiveFilters | null,
    ) => {
      useChatbotStore.getState().loadConversation(conversationId, conversationMessages, conversationFilters);
      setIsHistoryOpen(false);
    },
    [],
  );

  const injectedInputValue = injectedValue?.value;

  const handleDismissError = useCallback(() => {
    useChatbotStore.getState().setError(null);
  }, []);

  const welcomeMessageProps = useChatbotWelcome(handleQuestionClick);
  const filterHeaderGroups = useChatbotFilterHeader();
  const filterHeaderSlot = filterHeaderGroups ? (
    <ChatFilterHeader
      key={activeConversationId ?? 'new-conversation'}
      globalFilters={filterHeaderGroups.globalFilters}
      localFilters={filterHeaderGroups.localFilters}
    />
  ) : null;
  const hasConversation = messages.length > 0 || isStreaming;
  const resetAtMessage =
    rateLimit.resetAt !== null && rateLimit.remaining === 0
      ? t('components.aiChatDrawer.rateLimit.resetsAt', { time: formatResetTime(rateLimit.resetAt) })
      : null;
  const errorMessage = error ? joinMessages([error.message, resetAtMessage]) : undefined;
  const isRateLimitExhausted = rateLimit.remaining === 0;
  const conversationContent = hasConversation ? (
    <ChatConversationContent
      messages={messages}
      isStreaming={isStreaming}
      streamingContent={streamingContent}
      streamingFiles={streamingFiles}
      streamingReasoningSteps={streamingReasoningSteps}
      streamingPromptFilters={streamingPromptFilters}
      error={error}
      errorMessage={errorMessage}
      onDismissError={handleDismissError}
      onRetry={retryLastMessage}
    />
  ) : null;

  return (
    <AiChatDrawer isOpen={isDrawerOpen} onClose={handleDrawerClose} variant="fullHeight">
      <div className={styles.historyHost}>
        <ChatLayout
          userName={userInfo?.firstName ?? t('global.guest')}
          headerProps={{
            onClose: isHistoryOpen ? handleCloseHistory : handleDrawerClose,
            backButton: isHistoryOpen,
            onNewChat: isHistoryOpen ? handleHistoryNewChat : handleStartNewChat,
            newChatDisabled: isStreaming && !isHistoryOpen,
            showHistory: !isHistoryOpen,
            historyHidden: isHistoryOpen,
            historyDisabled: isStreaming,
            onHistoryClick: handleOpenHistory,
            historyButtonRef,
            title: isHistoryOpen ? t('components.aiChatDrawer.history.title') : undefined,
          }}
          inputProps={{
            onSend: handleSendMessage,
            onStop: stopMessage,
            isStreaming,
            isStopping,
            disabled: isRateLimitExhausted,
            submitDisabled: isStreaming || isStopping || isRateLimitExhausted || isConversationBusy,
            injectedValue: injectedInputValue,
            injectedValueKey: injectedValue?.key,
          }}
          bottomSlot={<RateLimitStatus />}
          filterHeaderSlot={filterHeaderSlot}
          onLearnMoreClick={handleDrawerClose}
          disclaimerStorageKey={FIRST_TIME_DISCLAIMER_SESSION_STORAGE_KEY}
          welcomeMessageProps={welcomeMessageProps}
        >
          {conversationContent}
        </ChatLayout>
        <ChatHistoryPanel isOpen={isHistoryOpen} onConversationLoaded={handleSelectConversation} />
      </div>
    </AiChatDrawer>
  );
};

export default ChatDrawer;
